import React from 'react';
import { AlertCircleIcon, ClockIcon, UsersIcon } from 'lucide-react';
import { useCountdown } from '../hooks/useCountdown';
import { offer } from '../data/offer';
interface UrgencyBannerProps {
  /** 'panel' sits inline under a CTA, 'bar' is a full-width sticky strip. */
  variant?: 'panel' | 'bar';
  className?: string;
}
function CountdownClock({
  compact = false
}: {
  compact?: boolean;
}) {
  const {
    hours,
    minutes,
    seconds,
    expired
  } = useCountdown(23.75);
  return <div className={`flex items-center gap-2 rounded-lg border border-white/10 bg-navy-800/80 px-3 py-1.5 tabular-nums ${compact ? '' : 'justify-center'}`} role="timer" aria-live="off" data-id="element-175" data-mp="319jw5nh3vr0n">
      {expired ? <span className="font-display text-sm font-bold text-sun" data-id="element-176" data-mp="3b8sn07gocpis">
          শেষ কয়েক ঘণ্টা — আজই দাম বাড়বে
        </span> : [{
      value: hours,
      unit: 'ঘণ্টা'
    }, {
      value: minutes,
      unit: 'মিনিট'
    }, {
      value: seconds,
      unit: 'সেকেন্ড'
    }].map((cell, index) => <React.Fragment key={cell.unit} data-id="element-177" data-mp="27egb66fovbj6" data-mpi={index}>
            {index > 0 ? <span className="text-white/20" aria-hidden="true" data-id="element-178" data-mp="27g15buy3csgi">
                :
              </span> : null}
            <span className="flex items-baseline gap-1" data-id="element-179" data-mp="22lwwf659macs">
              <span className="font-display text-base font-extrabold text-white" data-id="element-180" data-mp="1xpybjsj5bah4">
                {cell.value}
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/50" data-id="element-181" data-mp="1srn0jeh46txb">
                {cell.unit}
              </span>
            </span>
          </React.Fragment>)}
    </div>;
}
export function UrgencyBanner({
  variant = 'panel',
  className = ''
}: UrgencyBannerProps) {
  if (variant === 'bar') {
    return <div className="sticky top-0 z-50 border-b border-celeste-500/25 bg-navy-900/95 backdrop-blur" data-id="element-182" data-mp="2ce2yj8p8e85p">
        <div className="mx-auto flex max-w-content flex-col items-center gap-x-6 gap-y-2 px-4 py-2.5 text-center sm:px-6 lg:flex-row lg:justify-between lg:text-left" data-id="element-183" data-mp="14zzkxspaz5ac">
          <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[13px] text-white/80 sm:text-sm" data-id="element-184" data-mp="110qq0kij11d7">
            <span className="flex items-center gap-1.5 font-display text-xs font-extrabold uppercase tracking-[0.16em] text-sun" data-id="element-185" data-mp="17ew7m0fx9ii8">
              <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-186" data-mp="sif2hqybt044" />
              বিশেষ অফার — সীমিত সময়ের জন্য
            </span>
            <span className="hidden text-white/20 lg:inline" aria-hidden="true" data-id="element-187" data-mp="xevoaei1z2e1">
              |
            </span>
            <span className="flex items-center gap-1.5" data-id="element-188" data-mp="jnh3q38hyt4v">
              <AlertCircleIcon className="h-3.5 w-3.5 text-red-400" aria-hidden="true" data-id="element-189" data-mp="oh3flt6u6szm" />
              এই দামে মাত্র{' '}
              <span className="font-semibold text-white" data-id="element-190" data-mp="9syyuurcgrjh">{offer.spotsRemaining}টি স্পট</span>{' '}
              বাকি · কাল দাম বেড়ে{' '}
              <span className="font-semibold text-white" data-id="element-191" data-mp="qpny3s5yw5k">{offer.priceTomorrow}</span> হবে
            </span>
          </p>
          <div className="flex items-center gap-4" data-id="element-192" data-mp="4savrapuck8w">
            <p className="hidden items-center gap-1.5 text-[13px] text-white/60 xl:flex" data-id="element-193" data-mp="4u5gop144ccz">
              <UsersIcon className="h-3.5 w-3.5 text-celeste-400" aria-hidden="true" data-id="element-194" data-mp="3i9eop1w80vqk" />
              {offer.studentCount} শিক্ষার্থীর সাথে যোগ দিন
            </p>
            <CountdownClock compact data-id="element-195" data-mp="3raw4ej5puveq" />
          </div>
        </div>
      </div>;
  }
  return <div className={`rounded-xl border border-celeste-500/30 bg-navy-800/70 p-5 ${className}`} style={{
    paddingTop: "0px",
    paddingBottom: "0px"
  }} data-id="element-196" data-mp="3ma8fc311son5">
      <p className="flex items-center gap-2 font-display text-xs font-extrabold uppercase tracking-[0.16em] text-sun" data-id="element-197" data-mp="50idgbldm4rq">
        <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-198" data-mp="dux0x1g28llx" />
        বিশেষ অফার — সীমিত সময়ের জন্য
      </p>
      <p className="mt-3 flex items-start gap-2 text-[13px] leading-relaxed text-white/75" data-id="element-199" data-mp="9stejhrurwlv">
        <AlertCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" aria-hidden="true" data-id="element-200" data-mp="nsupxr5qumfl" />
        <span data-id="element-201" data-mp="iu9qvv28a8sq">
          এই দামে মাত্র <span className="font-semibold text-white" data-id="element-202" data-mp="iv2n423k9fwm">{offer.spotsRemaining}টি স্পট</span>{' '}
          বাকি · কাল দাম বেড়ে{' '}
          <span className="font-semibold text-white" data-id="element-203" data-mp="12vvwntcl5av6">{offer.priceTomorrow}</span> হবে
        </span>
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-3" data-id="element-204" data-mp="16vms4ad24f61">
        <CountdownClock compact data-id="element-205" data-mp="1bozh4llnenzb" />
        <p className="flex items-center gap-1.5 text-[13px] text-white/60" data-id="element-206" data-mp="1bqsf7ci73zvp">
          <UsersIcon className="h-3.5 w-3.5 text-celeste-400" aria-hidden="true" data-id="element-207" data-mp="1gmr5x6qzxtza" />
          {offer.studentCount} শিক্ষার্থীর সাথে যোগ দিন
        </p>
      </div>
    </div>;
}